import { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { IBuilding, IBuildingArea } from 'models';
import {
  useCreateBuildingMutation,
  useDeleteBuildingLogoMutation,
  useDeleteBuildingThumbnailMutation,
  useFetchBuildingQuery,
  useUpdateBuildingMutation,
} from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';

const FILE_SIZE = 10000000; // 10 MB
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

interface UseBuildingFormManagerProps {
  buildingId?: number | null;
  next?: (data?: any) => void;
}

type Image = {
  url?: string;
  file?: File | null;
};

export const useBuildingFormManager = ({ buildingId = null, next = () => {} }: UseBuildingFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [skipFetchBuilding, setSkipFetchBuilding] = useState<boolean>(true);
  const { data: building, isLoading: loading_form } = useFetchBuildingQuery(
    { orgSlug, id: buildingId as number },
    { skip: skipFetchBuilding },
  );
  const useMutation = building ? useUpdateBuildingMutation : useCreateBuildingMutation;
  const [mutate, { isLoading: loading_submit }] = useMutation();
  const [deleteThumbnail] = useDeleteBuildingThumbnailMutation();
  const [deleteLogo] = useDeleteBuildingLogoMutation();

  useEffect(() => {
    if (buildingId) {
      setSkipFetchBuilding(false);
    }
  }, [buildingId]);

  const initialValues = {
    account_name: building?.account_name || '',
    address: building?.address || '',
    city: building?.city || '',
    state: building?.state || '',
    zip: building?.zip || '',
    floors: building?.floors?.length || '',
    areas: building?.areas || [{ id: null, building_id: building?.id, name: 'Common' }],
    image: (building && building.thumbnail && building.thumbnail.url
      ? { url: building.thumbnail.url }
      : { url: '' }) as Image,
    logo: (building && building.logo && building.logo.url ? { url: building.logo.url } : { url: '' }) as Image,
    primary_brand_color: building?.primary_brand_color || '',
    secondary_brand_color: building?.secondary_brand_color || '',
    business_hours_start: building?.business_hours_start || '09:00am',
    business_hours_end: building?.business_hours_end || '05:00pm',
  };

  const validationSchema = Yup.object({
    account_name: Yup.string().nullable(),
    address: Yup.string().nullable().required('Building address is required.'),
    city: Yup.string().nullable().required('City is required'),
    state: Yup.string()
      .nullable()
      .required('State is required.')
      .min(2, 'State must be two characters.')
      .max(2, 'State must be two characters.'),
    zip: Yup.string()
      .nullable()
      .required('Zip code is required.')
      .matches(/\b\d{5}\b/, { message: 'Zip code must be five digits.', excludeEmptyString: true }),
    floors: Yup.number()
      .nullable()
      .required('Number of floors is required.')
      .min(1, 'This value must be greater than 0.'),
    areas: Yup.array()
      .of(Yup.object({ building_id: Yup.number(), name: Yup.string() }))
      .min(1),
    image: Yup.mixed()
      .test(
        'fileFormat',
        `Unsupported File Type. Valid types are ${SUPPORTED_FORMATS.join(', ')}`,
        ({ file }) => !file || (file && SUPPORTED_FORMATS.includes(file.type)),
      )
      .test('fileSize', 'File is larger than 10MB!', ({ file }) => !file || (file && file.size <= FILE_SIZE)),
    logo: Yup.mixed()
      .test(
        'fileFormat',
        `Unsupported File Type. Valid types are ${SUPPORTED_FORMATS.join(', ')}`,
        ({ file }) => !file || (file && SUPPORTED_FORMATS.includes(file.type)),
      )
      .test('fileSize', 'File is larger than 10MB!', ({ file }) => !file || (file && file.size <= FILE_SIZE)),
    business_hours_start: Yup.string().nullable(),
    business_hours_end: Yup.string().nullable(),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const {
        account_name,
        address,
        city,
        state,
        zip,
        floors,
        image,
        logo,
        primary_brand_color,
        secondary_brand_color,
        business_hours_start,
        business_hours_end,
        areas,
      } = values;

      const bodyFormData = new FormData();
      bodyFormData.append('account_name', account_name.trim());
      bodyFormData.append('address', address.trim());
      bodyFormData.append('city', city.trim());
      bodyFormData.append('state', state);
      bodyFormData.append('zip', `${zip}`.trim());
      bodyFormData.append('floors', `${floors}`.trim());
      bodyFormData.append('primary_brand_color', primary_brand_color);
      bodyFormData.append('secondary_brand_color', secondary_brand_color);
      bodyFormData.append('business_hours_start', `${business_hours_start}`);
      bodyFormData.append('business_hours_end', `${business_hours_end}`);
      areas.forEach((a: IBuildingArea) => {
        bodyFormData.append('areas[]', JSON.stringify(a));
      });

      if (image?.file) {
        bodyFormData.append('image', image.file);
      } else if (image?.file === null && building && building.id && building.thumbnail && building.thumbnail.url) {
        deleteThumbnail({ orgSlug, id: building.id });
      }

      if (logo?.file) {
        bodyFormData.append('logo', logo.file);
      } else if (logo?.file === null && building && building.id && building.logo && building.logo.url) {
        deleteLogo({ orgSlug, id: building.id });
      }

      let mutateProps: any = {
        orgSlug,
        building: bodyFormData,
      };

      if (building) {
        mutateProps = { ...mutateProps, id: building.id };
      }

      mutate(mutateProps)
        .then(({ data }: any) => {
          next(data);
        })
        .catch((err) => {
          console.error('error = ', err);
        })
        .finally(() => {});
    },
  });

  return [
    {
      building,
      formManager,
      loading_form,
      loading_submit,
    },
  ] as [
    {
      building: IBuilding | null;
      formManager: FormikProps<typeof initialValues>;
      loading_form: boolean;
      loading_submit: boolean;
    },
  ];
};
